import { useState, useRef } from 'react';
import {
  Title1,
  Text,
  Spinner,
  makeStyles,
  tokens,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { Class24Regular } from '@fluentui/react-icons';
import { TrackingFilters, type TrackingSearchParams } from '../../components/Logs/TrackingFilters';
import { OperationsTable, type OperationSummary } from '../../components/Logs/OperationsTable';
import { ClassSummaryBar, type ClassSummary } from '../../components/Logs/ClassSummaryBar';
import { logsService } from '../../services/logsService';
import type { LogEntry, OperationGroup } from '../../types';

const useStyles = makeStyles({
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '32px',
  },
  title: {
    marginBottom: '12px',
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
    display: 'block',
  },
  resultsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    marginTop: '24px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 48px',
    minHeight: '400px',
  },
  emptyStateIcon: {
    fontSize: '192px',
    color: 'rgba(30, 30, 30, 0.4)',
    marginBottom: '24px',
  },
  emptyStateText: {
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightSemibold,
  },
  emptyStateSubtext: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    marginTop: '8px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px',
  },
});

/**
 * Converte OperationGroup (API section) para OperationSummary (OperationsTable)
 * IMPORTANTE: Usa o ÚLTIMO evento para mostrar o status real da operação
 */
const convertToOperationSummary = (group: OperationGroup, sectionId: string): OperationSummary => {
  const startTime = new Date(group.startTime);
  const endTime = group.endTime ? new Date(group.endTime) : startTime;

  // Pegar o último evento para mostrar o status real da operação
  const lastEvent = group.events && group.events.length > 0
    ? group.events[group.events.length - 1]
    : null;

  return {
    correlationId: group.correlationId,
    operation: lastEvent?.operation || 'Unknown',
    messageType: group.messageType,
    startTime,
    endTime,
    duration: group.duration || '0ms',
    status: group.status,
    totalEvents: group.eventCount,
    errorCount: group.events?.filter(e => e.level === 'Error').length || 0,
    warningCount: group.events?.filter(e => e.level === 'Warning').length || 0,
    externalId: group.userExternalId,
    displayName: group.userDisplayName,
    userPrincipalName: group.userPrincipalName,
    hasDlqMessage: group.hasDlq,
    maxRetryCount: group.maxRetryCount,
    hasRetries: group.hasRetries,
    hasDlq: group.hasDlq,
    events: group.events || [],
    sectionId,
  };
};

/**
 * Filtra operações por status baseado no último evento
 * @param operations Lista de operações
 * @param statusFilter Filtro de status: 'success' | 'error' | 'retry' | 'skipped'
 */
const filterByStatus = (operations: OperationSummary[], statusFilter: string): OperationSummary[] => {
  return operations.filter(op => {
    switch (statusFilter) {
      case 'success':
        return op.status === 'success';
      case 'error':
        return op.status === 'error';
      case 'retry':
        return op.status === 'processing'; // Retry = processing
      case 'skipped':
        return op.status === 'warning'; // Ignorado = warning (GRAPH_CALL_SKIPPED)
      default:
        return true;
    }
  });
};

/**
 * Agrupa eventos por correlationId (para busca por correlationId)
 */
const groupLogsByCorrelation = (logs: LogEntry[]): OperationSummary[] => {
  const grouped = new Map<string, Array<LogEntry & { originalIndex: number }>>();

  logs.forEach((log, index) => {
    const existing = grouped.get(log.correlationId) || [];
    existing.push({ ...log, originalIndex: index });
    grouped.set(log.correlationId, existing);
  });

  const summaries: OperationSummary[] = [];

  grouped.forEach((events, correlationId) => {
    events.sort((a, b) => {
      const timeA = new Date(a.timeGenerated).getTime();
      const timeB = new Date(b.timeGenerated).getTime();
      if (timeA !== timeB) return timeA - timeB;
      return a.originalIndex - b.originalIndex;
    });

    const firstEvent = events[0];
    const lastEvent = events[events.length - 1];
    const errorCount = events.filter(e => e.level === 'Error').length;
    const warningCount = events.filter(e => e.level === 'Warning').length;

    // Determinar status baseado no último evento (operation)
    const lastOperation = lastEvent.operation?.toUpperCase() || '';
    let status: 'success' | 'warning' | 'error' | 'processing' = 'success';

    if (lastOperation === 'GRAPH_CALL_SUCCESS') {
      status = 'success';
    } else if (lastOperation === 'GRAPH_CALL_FAILED' || lastOperation === 'SENT_TO_DLQ' || lastOperation === 'UNHANDLED_EXCEPTION') {
      status = 'error';
    } else if (lastOperation === 'RETRY_SCHEDULED') {
      status = 'processing'; // Retry
    } else if (lastOperation === 'GRAPH_CALL_SKIPPED') {
      status = 'warning'; // Ignorado/Skipped
    } else if (errorCount > 0) {
      status = 'error';
    } else if (warningCount > 0) {
      status = 'warning';
    }

    const getTimestamp = (event: LogEntry) => new Date(event.operationTimestamp || event.timeGenerated);
    const startTime = getTimestamp(firstEvent);
    const endTime = getTimestamp(lastEvent);
    const durationMs = endTime.getTime() - startTime.getTime();

    let duration: string;
    if (durationMs < 1000) {
      duration = `${durationMs}ms`;
    } else if (durationMs < 60000) {
      duration = `${(durationMs / 1000).toFixed(2)}s`;
    } else {
      duration = `${(durationMs / 60000).toFixed(2)}min`;
    }

    const validEvents = events.filter(e => e.operation && e.operation.trim() !== '');
    const maxRetryCount = Math.max(...events.map(e => e.retryCount || 0));
    const hasRetries = maxRetryCount > 0;
    const hasDlq = events.some(e => e.operation?.toUpperCase() === 'SENT_TO_DLQ');

    // Para operações de enrollment/roster, usar userExternalId; senão usar externalId
    const userExternalId = firstEvent.userExternalId || firstEvent.externalId;
    // Pegar sectionExternalId para operações que envolvem turma
    const sectionExternalId = firstEvent.sectionExternalId;

    summaries.push({
      correlationId,
      operation: firstEvent.operation || 'Unknown',
      messageType: firstEvent.messageType || 'Unknown',
      startTime,
      endTime,
      duration,
      status,
      totalEvents: validEvents.length,
      errorCount,
      warningCount,
      externalId: userExternalId,
      displayName: firstEvent.displayName,
      userPrincipalName: firstEvent.userPrincipalName,
      hasDlqMessage: false,
      maxRetryCount,
      hasRetries,
      hasDlq,
      events,
      sectionId: sectionExternalId,
    });
  });

  return summaries;
};

export const SectionTrackingPage: React.FC = () => {
  const styles = useStyles();

  // Estados
  const [operations, setOperations] = useState<OperationSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [classSummary, setClassSummary] = useState<ClassSummary | null>(null);

  // Guardar parâmetros da última busca
  const lastSearchParams = useRef<TrackingSearchParams | null>(null);

  /**
   * Handler de busca
   */
  const handleSearch = async (params: TrackingSearchParams) => {
    setLoading(true);
    setError(null);
    setHasSearched(true);
    setOperations([]);
    setClassSummary(null);
    lastSearchParams.current = params;
    setStatusFilter(params.status);

    try {
      // Se tem correlationId, busca por ele especificamente
      if (params.correlationId) {
        const logs = await logsService.getByCorrelationId(params.correlationId);
        const grouped = groupLogsByCorrelation(logs);

        // Aplicar filtro de operação se especificado
        let filtered = grouped;
        if (params.operation) {
          filtered = filtered.filter(op => op.messageType === params.operation);
        }

        // Aplicar filtro de status se especificado
        if (params.status) {
          filtered = filterByStatus(filtered, params.status);
        }

        setOperations(filtered);
        return;
      }

      // Busca por sectionId
      const sectionId = params.sectionId?.trim();
      if (!sectionId) {
        setError('Digite o ID da classe ou selecione uma classe pelo nome');
        return;
      }

      // Buscar timeline enriquecida da seção
      const timeline = await logsService.getEnrichedBySectionId(sectionId, 5000);

      // Filtrar por data se necessário
      let filteredOperations = timeline.operations;
      if (params.dateFrom || params.dateTo) {
        filteredOperations = timeline.operations.filter(op => {
          const opDate = new Date(op.startTime);
          if (params.dateFrom && opDate < params.dateFrom) return false;
          if (params.dateTo && opDate > params.dateTo) return false;
          return true;
        });
      }

      // Filtrar por operação se especificado
      if (params.operation) {
        filteredOperations = filteredOperations.filter(op => op.messageType === params.operation);
      }

      // Converter para OperationSummary
      let converted = filteredOperations.map(op => convertToOperationSummary(op, sectionId));

      // Aplicar filtro de status se especificado
      if (params.status) {
        converted = filterByStatus(converted, params.status);
      }

      setOperations(converted);

      // Recalcular métricas após filtros
      const successCount = filteredOperations.filter(o => o.status === 'success').length;
      const errorCount = filteredOperations.filter(o => o.status === 'error').length;
      const warningCount = filteredOperations.filter(o => o.status === 'warning').length;
      const totalUsers = new Set(filteredOperations.map(o => o.userExternalId)).size;

      // Calcular datas
      let firstEvent: string | undefined;
      let lastEvent: string | undefined;
      if (filteredOperations.length > 0) {
        const timestamps = filteredOperations.map(o => new Date(o.startTime).getTime());
        firstEvent = new Date(Math.min(...timestamps)).toISOString();
        lastEvent = new Date(Math.max(...timestamps)).toISOString();
      }

      // Atualizar resumo
      setClassSummary({
        sectionId,
        sectionDisplayName: timeline.sectionDisplayName,
        classCode: timeline.classCode,
        totalOperations: filteredOperations.length,
        totalUsers,
        successCount,
        errorCount,
        warningCount,
        firstEvent,
        lastEvent,
      });

    } catch (err: any) {
      console.error('Erro ao buscar classe:', err);
      if (err.response?.status === 404) {
        setError(`Nenhum evento encontrado para a classe: ${params.sectionId}`);
      } else {
        setError('Erro ao buscar eventos da classe. Verifique se o ID está correto.');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handler para limpar filtros
   */
  const handleClear = () => {
    setOperations([]);
    setHasSearched(false);
    setError(null);
    setClassSummary(null);
    lastSearchParams.current = null;
    setStatusFilter(undefined);
  };

  /**
   * Handler para carregar eventos de uma operação específica
   */
  const handleLoadEvents = async (correlationId: string): Promise<LogEntry[]> => {
    try {
      const logs = await logsService.getByCorrelationId(correlationId);

      setOperations(prev =>
        prev.map(op => {
          if (op.correlationId === correlationId) {
            return { ...op, events: logs };
          }
          return op;
        })
      );

      return logs;
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
      throw error;
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Title1 className={styles.title}>Rastreamento por Classe</Title1>
        <Text className={styles.subtitle}>
          Visualize todos os eventos de matrícula, professores e staff de uma classe específica
        </Text>
      </div>

      {/* Filtros (REUTILIZADO com mode='class') */}
      <TrackingFilters
        mode="class"
        onSearch={handleSearch}
        onClear={handleClear}
        loading={loading}
      />

      {/* Error State */}
      {error && (
        <MessageBar intent="error" style={{ marginTop: '16px' }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {/* Loading State */}
      {loading && (
        <div className={styles.loadingContainer}>
          <Spinner label="Buscando eventos da classe..." size="large" />
        </div>
      )}

      {/* Resumo da classe (se buscada) */}
      {!loading && classSummary && (
        <ClassSummaryBar summary={classSummary} />
      )}

      {/* Results */}
      {!loading && hasSearched && (
        <>
          {operations.length > 0 ? (
            <>
              <div className={styles.resultsHeader}>
                <Text weight="semibold" size={400}>
                  {operations.length} {operations.length === 1 ? 'operação encontrada' : 'operações encontradas'}
                </Text>
              </div>
              <OperationsTable
                operations={operations}
                onLoadEvents={handleLoadEvents}
                statusFilter={statusFilter}
              />
            </>
          ) : !error && (
            <div className={styles.emptyState}>
              <Text className={styles.emptyStateText}>Nenhum evento encontrado</Text>
              <Text className={styles.emptyStateSubtext}>
                Verifique se o ID da classe está correto ou ajuste os filtros de data
              </Text>
            </div>
          )}
        </>
      )}

      {/* Initial State */}
      {!loading && !hasSearched && (
        <div className={styles.emptyState}>
          <Class24Regular className={styles.emptyStateIcon} />
          <Text className={styles.emptyStateText}>Busque uma classe</Text>
          <Text className={styles.emptyStateSubtext}>
            Digite o ID ou nome da classe para ver todos os eventos de matrícula
          </Text>
        </div>
      )}
    </div>
  );
};
